import { primaryDueBadge } from "@icraftx/shared/v1";

/** Sticky rail primary verb — plan/35 Create ▾. */
export type DeskDocPrimaryVerb =
  | "deliver"
  | "collect"
  | "receive"
  | "pay";

export type SoCreateKind =
  | "invoice"
  | "in-transit"
  | "delivery"
  | "payment"
  | "return";
export type PoCreateKind = "invoice" | "receipt" | "payment";

/**
 * Most urgent Collect/Deliver (or Pay/Receive) for the rail primary.
 * Remaining axes only; when both open, earlier due wins; tie → fulfill first.
 */
export function pickSoPrimaryVerb(input: {
  pay: string;
  deliver: string;
  paymentDueOn?: string | null;
  deliverBy?: string | null;
  now?: Date;
  timeZone?: string;
}): DeskDocPrimaryVerb | null {
  const needDeliver = input.deliver !== "delivered";
  const needCollect = input.pay !== "paid";
  if (!needDeliver && !needCollect) return null;
  if (needDeliver && !needCollect) return "deliver";
  if (!needDeliver && needCollect) return "collect";

  const payBadge = primaryDueBadge(
    { paymentDueOn: input.paymentDueOn },
    { now: input.now, timeZone: input.timeZone },
  );
  const delivBadge = primaryDueBadge(
    { deliverBy: input.deliverBy },
    { now: input.now, timeZone: input.timeZone },
  );
  if (payBadge.days != null && delivBadge.days != null) {
    if (payBadge.days < delivBadge.days) return "collect";
    if (delivBadge.days < payBadge.days) return "deliver";
  } else if (payBadge.days != null && delivBadge.days == null) {
    return "collect";
  } else if (delivBadge.days != null && payBadge.days == null) {
    return "deliver";
  }
  return "deliver";
}

export function pickPoPrimaryVerb(input: {
  pay: string;
  receive: string;
  paymentDueOn?: string | null;
  expectedOn?: string | null;
  now?: Date;
  timeZone?: string;
}): DeskDocPrimaryVerb | null {
  const needReceive = input.receive !== "received";
  const needPay = input.pay !== "paid";
  if (!needReceive && !needPay) return null;
  if (needReceive && !needPay) return "receive";
  if (!needReceive && needPay) return "pay";

  const payBadge = primaryDueBadge(
    { paymentDueOn: input.paymentDueOn },
    { now: input.now, timeZone: input.timeZone },
  );
  const recvBadge = primaryDueBadge(
    { expectedOn: input.expectedOn },
    { now: input.now, timeZone: input.timeZone },
  );
  if (payBadge.days != null && recvBadge.days != null) {
    if (payBadge.days < recvBadge.days) return "pay";
    if (recvBadge.days < payBadge.days) return "receive";
  } else if (payBadge.days != null && recvBadge.days == null) {
    return "pay";
  } else if (recvBadge.days != null && payBadge.days == null) {
    return "receive";
  }
  return "receive";
}

/** State-aware Create ▾ entries for an open SO — plan/35 + plan/30 in-transit. */
export function soCreateKinds(input: {
  primary: DeskDocPrimaryVerb | null;
  pay: string;
  deliver: string;
  qtyDelivered: string | number;
  /** pickup skips In transit — plan/30 */
  fulfillmentMethod?: "pickup" | "delivery" | null;
}): SoCreateKind[] {
  const out: SoCreateKind[] = ["invoice"];
  if (
    input.deliver !== "delivered" &&
    input.fulfillmentMethod !== "pickup"
  ) {
    out.push("in-transit");
  }
  if (input.deliver !== "delivered" && input.primary !== "deliver") {
    out.push("delivery");
  }
  if (input.pay !== "paid" && input.primary !== "collect") {
    out.push("payment");
  }
  if (Number(input.qtyDelivered) > 0) {
    out.push("return");
  }
  return out;
}

export function poCreateKinds(input: {
  primary: DeskDocPrimaryVerb | null;
  pay: string;
  receive: string;
}): PoCreateKind[] {
  const out: PoCreateKind[] = ["invoice"];
  if (input.receive !== "received" && input.primary !== "receive") {
    out.push("receipt");
  }
  if (input.pay !== "paid" && input.primary !== "pay") {
    out.push("payment");
  }
  return out;
}
